import { Actions, getPdfShareLink, selectors, setCustomerCardStateAction } from '@makemydeal/dr-sales-checkin-store';
import {
    CustomerCardState,
    ConversationTypeEnum,
    DeepLinks,
    FEATURES,
    type FeatureName,
    type IShowroomCommand
} from '@makemydeal/dr-sales-checkin-types';

import {
    Cobuyer,
    CobuyerInbox,
    CobuyerInfo,
    DealSummary,
    Finance,
    Inbox,
    Insurance,
    InventoryPath,
    ManageDocumentsPath,
    RecentDeals,
    RemoveCoBuyer,
    Tradein,
    Username,
    VehicleDetails
} from '../../constants';
import type { CommandBuilderSet, NavViewContext } from '../../types/showroomNavTypes';

/**
 * Builds an {@link IShowroomCommand}. When not executable the handler is dropped
 * (replaced by a no-op) so a disabled item can never fire its action.
 */
export const cmd = (canExecute: boolean, execute?: () => void): IShowroomCommand => ({
    canExecute,
    execute: canExecute && execute ? execute : () => undefined
});

// Share deep-link dispatchers (moved out of SendLinkDropdown so behaviour is
// config/command-driven rather than baked into the component).
const openShareDeepLink = (ctx: NavViewContext, cdlType: string, message: DeepLinks) => {
    ctx.dispatch({ type: cdlType });
    ctx.dispatch(
        setCustomerCardStateAction(CustomerCardState.INBOX, {
            defaultTab: 'inbox',
            occDefaultTab: ConversationTypeEnum.TEXT,
            message
        })
    );
};

/**
 * Per-entity command builders. Each turns the live {@link NavViewContext} into the
 * `IShowroomCommand`(s) an item needs. Reuses the existing `useNavigationHandler`
 * handlers so click behaviour stays in one place. `canExecute` here is the base
 * (always true); `buildNavViewItems` gates it further with the disable rules.
 */
export const commandBuildersByKey: Partial<Record<FeatureName, CommandBuilderSet>> = {
    // Top nav
    [FEATURES.BUYER]: { click: (ctx) => cmd(true, ctx.handlers[Username]) },
    [FEATURES.BUYER_INBOX]: { click: (ctx) => cmd(true, ctx.handlers[Inbox]) },
    [FEATURES.ADD_COBUYER]: { add: (ctx) => cmd(true, ctx.handlers[Cobuyer]) },
    [FEATURES.COBUYER]: {
        click: (ctx) => cmd(true, ctx.handlers[CobuyerInfo]),
        remove: (ctx) => cmd(true, ctx.handlers[RemoveCoBuyer])
    },
    [FEATURES.COBUYER_INBOX]: { click: (ctx) => cmd(true, ctx.handlers[CobuyerInbox]) },
    // Share dropdown
    [FEATURES.CREDIT_APP]: {
        click: (ctx) => cmd(true, () => openShareDeepLink(ctx, Actions.SEND_CREDIT_APP_LINK_CDL, DeepLinks.CreditApp))
    },
    [FEATURES.DOC_UPLOAD]: {
        click: (ctx) => cmd(true, () => openShareDeepLink(ctx, Actions.SEND_DOC_UPLOAD_LINK_CDL, DeepLinks.DocUpload))
    },
    // Deal PDF's share link (unlike creditApp/docUpload) isn't fetched ahead of time —
    // `getPdfShareLink` itself opens the inbox, either immediately from a cached link for
    // the current dealPdfVersion, or after re-minting one via the BFF/S3/Short.io.
    [FEATURES.DEAL_PDF]: {
        click: (ctx) =>
            cmd(true, () => {
                ctx.dispatch({ type: Actions.SEND_DEAL_PDF_LINK_CDL });
                ctx.dispatch(getPdfShareLink(selectors));
            })
    },
    // Left nav
    [FEATURES.VEHICLE_DETAILS]: { click: (ctx) => cmd(true, ctx.handlers[VehicleDetails]) },
    [FEATURES.TRADE_IN]: { click: (ctx) => cmd(true, ctx.handlers[Tradein]) },
    [FEATURES.CUSTOMER_DETAILS]: { click: (ctx) => cmd(true, ctx.handlers[Finance]) },
    [FEATURES.DEAL_SUMMARY]: { click: (ctx) => cmd(true, ctx.handlers[DealSummary]) },
    // Quick links
    [FEATURES.BROWSE_INVENTORY]: { click: (ctx) => cmd(true, ctx.handlers[InventoryPath]) },
    [FEATURES.MANAGE_DOCUMENTS]: { click: (ctx) => cmd(true, ctx.handlers[ManageDocumentsPath]) },
    [FEATURES.RECENT_DEALS]: { click: (ctx) => cmd(true, ctx.handlers[RecentDeals]) },
    // Opens Customer Details (financing) with the Insurance sub-tab pre-selected via handleInsurance.
    [FEATURES.INSURANCE]: { click: (ctx) => cmd(true, ctx.handlers[Insurance]) }
};
